import { LitElement, html, css, nothing } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { api } from "../api";
import { filterIgnoredMfes } from "../snapshot-utils";
import { filterMfeNames } from "../product-utils";
import { loadPresets } from "../preset-utils";
import type { EnvironmentsResponse, ProductsResponse } from "../types";

import "../components/preset-manager";

@customElement("presets-page")
export class PresetsPage extends LitElement {
  static styles = css`
    :host {
      display: block;
      font-family: Geist, sans-serif;
    }

    bee-card::part(body) {
      padding: var(--spacing-20, 20px);
    }

    .empty {
      color: var(--colors-foreground-fg-gray-700, #5a6072);
      text-align: center;
      font-size: 13px;
      padding: var(--spacing-24, 24px);
    }

    .empty-state-card {
      --padding: var(--spacing-20, 20px);
    }

    .empty-state {
      display: flex;
      flex-direction: column;
      gap: var(--spacing-12, 12px);
    }

    .empty-state-title {
      font-size: 18px;
      font-weight: 600;
      color: var(--colors-text-brand-900, #002266);
    }

    .empty-state-copy {
      color: var(--colors-foreground-fg-gray-700, #5a6072);
      font-size: 13px;
      line-height: 1.5;
    }

    .empty-state-list {
      margin: 0;
      padding-left: 18px;
      color: var(--colors-foreground-fg-gray-700, #5a6072);
      font-size: 13px;
      line-height: 1.6;
    }
  `;

  @property({ attribute: false }) environments: EnvironmentsResponse | null = null;
  @property({ attribute: false }) activeProductFilter: string | null = null;
  @property({ attribute: false }) products: ProductsResponse | null = null;

  @state() private _availableMfes: string[] = [];
  @state() private _loading = false;
  @state() private _error = "";
  @state() private _hasPresets = false;

  async connectedCallback() {
    super.connectedCallback();
    this._hasPresets = loadPresets().length > 0;
    await this._loadAvailableMfes();
  }

  private async _loadAvailableMfes() {
    this._loading = true;
    try {
      const data = this.environments ?? await api.getEnvironments();
      const allMfes = new Set<string>();
      for (const env of ["dev", "stage", "prod"] as const) {
        for (const name of Object.keys(filterIgnoredMfes(data[env].mfes))) {
          allMfes.add(name);
        }
      }
      const mfeNames = [...allMfes].sort();
      this._availableMfes = filterMfeNames(mfeNames, this.activeProductFilter, this.products);
    } catch (e: unknown) {
      this._error = e instanceof Error ? e.message : "Failed to load MFE list";
    } finally {
      this._loading = false;
    }
  }

  render() {
    if (this._loading) {
      return html`<div class="empty"><bee-spinner size="medium" type="blue"></bee-spinner></div>`;
    }

    return html`
      ${this._error ? html`<bee-alert variant="error" open closable><span slot="message">${this._error}</span></bee-alert>` : nothing}
      ${!this._hasPresets ? this._renderEmptyStateCard() : nothing}
      <preset-manager
        .availableMfes=${this._availableMfes}
        .showEmptyState=${false}
        @preset-count-changed=${this._handlePresetCountChanged}
      ></preset-manager>
    `;
  }

  private _handlePresetCountChanged(e: CustomEvent<{ count: number }>) {
    this._hasPresets = e.detail.count > 0;
  }

  private _renderEmptyStateCard() {
    return html`
      <bee-card class="empty-state-card" type="neutral">
        <div class="empty-state">
          <div class="empty-state-title">No presets saved yet</div>
          <div class="empty-state-copy">
            Presets let you save a reusable set of MFEs so promotions and comparisons start from the same selection every time.
          </div>
          <ol class="empty-state-list">
            <li>Open the Versions page for the product you want to work with.</li>
            <li>Select the MFEs you reuse most often for stage or prod promotions.</li>
            <li>Use Save Selection as Preset, then return here to rename or maintain that preset.</li>
          </ol>
        </div>
      </bee-card>
    `;
  }
}
